import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { fetchBenchmarksData, fetchFilterOptions, fetchMetricOptions } from '@/services/api';

// Helper function to get label/type from backend metric options
function getMetricLabel(key, metricOptions) {
  return metricOptions?.y_axis?.find(opt => opt.value === key)?.label || key;
}

function getMetricType(key, metricOptions) {
  return metricOptions?.y_axis?.find(opt => opt.value === key)?.type || 'number';
}

function buildFilterArrays(campaign_name, poc_id, poc_name) {
  const campaignArray = Array.isArray(campaign_name)
    ? campaign_name
    : (campaign_name ? [campaign_name] : []);

  const pocIdArray = Array.isArray(poc_id)
    ? poc_id
    : (poc_id ? [poc_id] : []);

  const pocNameArray = Array.isArray(poc_name)
    ? poc_name
    : (poc_name ? [poc_name] : []);

  return { campaignArray, pocIdArray, pocNameArray };
}

export function useBenchmarksData({ region, brand, campaign_name, start_date, end_date, poc_id, poc_name, key_account }) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [metricOptions, setMetricOptions] = useState({ y_axis: [] });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const stringCompare = (a, b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  // Create stable string representation of poc_id array to prevent infinite loops
  const pocIdKey = useMemo(() => {
    const pocIdArray = Array.isArray(poc_id) ? poc_id : (poc_id ? [poc_id] : []);
    return JSON.stringify([...pocIdArray].sort(stringCompare));
  }, [poc_id]);

  // Create stable string representation of campaign array to prevent infinite loops
  const campaignKey = useMemo(() => {
    const campaignArray = Array.isArray(campaign_name) ? campaign_name : (campaign_name ? [campaign_name] : []);
    return JSON.stringify([...campaignArray].sort(stringCompare));
  }, [campaign_name]);

  // Create stable string representation of poc_name array to prevent infinite loops
  const pocNameKey = useMemo(() => {
    const pocNameArray = Array.isArray(poc_name) ? poc_name : (poc_name ? [poc_name] : []);
    return JSON.stringify([...pocNameArray].sort(stringCompare));
  }, [poc_name]);

  // Track previous filter values to prevent unnecessary re-fetches
  const prevFiltersRef = useRef({});

  // Fetch metric options from backend (context='benchmarks')
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const data = await fetchMetricOptions("benchmarks");
        setMetricOptions(data);
      } catch (err) {
        console.error('Error fetching metric options:', err);
      }
    };
    fetchMetrics();
  }, []);

  // Fetch filter options when filters change
  useEffect(() => {
    // Create a key to compare filter values
    const filterKey = JSON.stringify({
      region,
      brand,
      campaign: campaignKey,
      start_date,
      end_date,
      poc_id: pocIdKey,
      poc_name: pocNameKey,
      key_account
    });

    // Only fetch if filters actually changed (or on initial mount)
    if (prevFiltersRef.current.filterKey === filterKey && prevFiltersRef.current.filterKey !== undefined) {
      return;
    }

    prevFiltersRef.current.filterKey = filterKey;

    const fetchFilters = async () => {
      try {
        const { campaignArray, pocIdArray, pocNameArray } = buildFilterArrays(campaign_name, poc_id, poc_name);
        const data = await fetchFilterOptions(
          region,
          brand,
          campaignArray.length > 0 ? campaignArray : undefined,
          start_date,
          end_date,
          pocIdArray.length > 0 ? pocIdArray : undefined,
          pocNameArray.length > 0 ? pocNameArray : undefined,
          key_account
        );
        setFilterOptions(data || {});
      } catch (err) {
        console.error('Error fetching filter options:', err);
      }
    };
    fetchFilters();
  }, [region, brand, campaignKey, start_date, end_date, pocIdKey, pocNameKey, key_account]);

  // Memoized fetch function with stable dependencies (removed page and pageSize from deps)
  // page and pageSize are passed as parameters, so they don't need to be in the dependency array
  const fetchData = useCallback(async (customPage, customPageSize, isPagination = false) => {
    if (isPagination) {
      setIsTableLoading(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const { campaignArray, pocNameArray } = buildFilterArrays(campaign_name, poc_id, poc_name);
      const benchmarksData = await fetchBenchmarksData({ 
        region, 
        brand, 
        campaign_name: campaignArray.length > 0 ? campaignArray : undefined, 
        start_date, 
        end_date, 
        poc_id, 
        poc_name: pocNameArray.length > 0 ? pocNameArray : undefined,
        key_account, 
        page: customPage, 
        page_size: customPageSize 
      });
      setData(benchmarksData);
      setTotalRows(benchmarksData.deviation_table_total_rows || 0);
      setTotalPages(Math.ceil((benchmarksData.deviation_table_total_rows || 0) / customPageSize) || 1);
    } catch (err) {
      setError(err);
    } finally {
      if (isPagination) {
        setIsTableLoading(false);
      } else {
        setIsLoading(false);
      }
    }
  }, [
    region, 
    brand, 
    campaign_name, 
    start_date, 
    end_date, 
    poc_id,
    key_account
  ]);

  // Single effect to handle both filter changes and pagination
  // fetchData is stable (only changes when filters change), page and pageSize trigger new fetches
  useEffect(() => {
    const isPagination = page !== 1 || pageSize !== 20;
    fetchData(page, pageSize, isPagination);
  }, [fetchData, page, pageSize]);

  // Function to fetch all deviation table data for export
  const getAllBenchmarksData = useCallback(async () => {
    const { campaignArray, pocIdArray, pocNameArray } = buildFilterArrays(campaign_name, poc_id, poc_name);
    const allData = await fetchBenchmarksData({
      region,
      brand,
      campaign_name: campaignArray.length > 0 ? campaignArray : undefined,
      start_date,
      end_date,
      poc_id: pocIdArray.length > 0 ? pocIdArray : undefined,
      poc_name: pocNameArray.length > 0 ? pocNameArray : undefined,
      key_account,
      page: 1,
      page_size: 100000
    });
    return allData;
  }, [region, brand, campaign_name, start_date, end_date, poc_id, key_account]);

  return {
    data,
    isLoading,
    isTableLoading,
    error,
    filterOptions,
    metricOptions,
    getMetricLabel,
    getMetricType,
    refetch: fetchData,
    page,
    setPage,
    pageSize,
    setPageSize,
    totalRows,
    totalPages,
    getAllBenchmarksData
  };
}